import { Form, Space } from 'antd';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Button } from '../../components';
import AddressForm from '../../components/AddressForm';
import Tag from '../../components/Tag';
import classNames from './EditableUserAddressPanel.module.scss';

const EditableUserAddressPanel = ({ t, userDetails, onAddressChange, isLoading }) => {
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [customAddress, setCustomAddress] = useState(null);

  const supportedCountries = useSelector(store => store.common.supportedCountries);

  const filteredCountries = (supportedCountries || []).filter(
    country => country.code === 'US' || country.code === 'CA',
  );

  const originalAddress = userDetails?.personalInfor?.homeAddress;

  const areAddressesEqual = (addr1, addr2) => {
    if (!addr1 || !addr2) return false;

    const normalize = str => (str || '').trim().toLowerCase();

    return (
      normalize(addr1.streetOne) === normalize(addr2.streetOne) &&
      normalize(addr1.city) === normalize(addr2.city) &&
      normalize(addr1.state) === normalize(addr2.state) &&
      normalize(addr1.postalCode) === normalize(addr2.postalCode) &&
      normalize(addr1.country) === normalize(addr2.country)
    );
  };

  useEffect(() => {
    setCustomAddress(null);
    setIsEditing(false);
    const userHomeAddress = userDetails?.personalInfor?.homeAddress;
    if (userHomeAddress) {
      form.setFieldsValue({
        streetOne: userHomeAddress.streetOne || '',
        city: userHomeAddress.city || '',
        state: userHomeAddress.state || '',
        postalCode: userHomeAddress.postalCode || '',
        country: userHomeAddress.country || '',
      });
    }
  }, [userDetails, form]);

  const currentAddress = customAddress || originalAddress || {};
  const { streetOne, city, state, postalCode, country } = currentAddress;

  const hasAddressData = streetOne || city || state || postalCode || country;

  const hasCustomAddress =
    customAddress !== null && !areAddressesEqual(customAddress, originalAddress);

  const handleAddressFieldChange = updatedFields => {
    const updatedAddress = {
      ...originalAddress,
      ...customAddress,
      ...updatedFields,
    };
    setCustomAddress(updatedAddress);

    if (areAddressesEqual(updatedAddress, originalAddress)) {
      onAddressChange(null);
    } else {
      onAddressChange(updatedAddress);
    }
  };

  const handleResetToOriginal = () => {
    setCustomAddress(null);
    setIsEditing(false);
    onAddressChange(null);
    if (originalAddress) {
      form.setFieldsValue({
        streetOne: originalAddress.streetOne || '',
        city: originalAddress.city || '',
        state: originalAddress.state || '',
        postalCode: originalAddress.postalCode || '',
        country: originalAddress.country || '',
      });
    }
  };

  const handleToggleEdit = () => {
    if (isEditing && !hasCustomAddress) {
      setIsEditing(false);
    } else {
      setIsEditing(!isEditing);
    }
  };

  return (
    <div className={classNames.editableUserAddressPanel}>
      <Space direction="vertical" size="small" className={classNames.content}>
        <div className={classNames.header}>
          <strong>{t('userAddress')}</strong>
          {hasAddressData && (
            <div className={classNames.actions}>
              {hasCustomAddress && (
                <Button
                  type="secondary"
                  size="xs"
                  onClick={handleResetToOriginal}
                  disabled={isLoading}
                  className={classNames.resetButton}
                >
                  {t('resetToOriginal')}
                </Button>
              )}
              <Button type="secondary" size="xs" onClick={handleToggleEdit} disabled={isLoading}>
                {isEditing ? t('done') : t('edit')}
              </Button>
            </div>
          )}
        </div>

        {hasAddressData ? (
          <>
            {isEditing ? (
              <div className={classNames.formFields}>
                <AddressForm
                  t={t}
                  formInstance={form}
                  values={customAddress || originalAddress || {}}
                  countries={filteredCountries}
                  onValueChange={handleAddressFieldChange}
                  formDisabled={isLoading}
                  allFieldsOptional={true}
                  fieldNames={{}}
                  hiddenFields={['streetTwo']}
                />
              </div>
            ) : (
              <div className={classNames.displayMode}>
                {streetOne && <div>{streetOne}</div>}
                <div>{[city, state, postalCode].filter(Boolean).join(', ')}</div>
                {country && <div>{country}</div>}
                {hasCustomAddress && (
                  <Tag variant="orange" style={{ marginTop: '5px' }}>
                    {t('customAddress')}
                  </Tag>
                )}
              </div>
            )}
          </>
        ) : (
          <div className={classNames.noData}>{t('noAddressAvailable')}</div>
        )}

        {userDetails?.email && (
          <>
            <div className={classNames.subheader}>
              <strong>{t('email')}</strong>
            </div>
            <p className={classNames.email} title={userDetails?.email}>
              {userDetails?.email}
            </p>
          </>
        )}
      </Space>
    </div>
  );
};

EditableUserAddressPanel.propTypes = {
  t: PropTypes.func.isRequired,
  userDetails: PropTypes.shape({
    personalInfor: PropTypes.shape({
      homeAddress: PropTypes.shape({
        streetOne: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        postalCode: PropTypes.string,
        country: PropTypes.string,
      }),
    }),
  }),
  onAddressChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

EditableUserAddressPanel.defaultProps = {
  userDetails: null,
  isLoading: false,
};

export default EditableUserAddressPanel;
